import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core'
import { Router } from '@angular/router'
import { AuthService } from '#root/core/auth/auth.service'
import { Subject, fromEvent, merge, takeUntil } from 'rxjs'
import { debounceTime } from 'rxjs/operators'

const COUNTDOWN_SECONDS = 600
const YELLOW_THRESHOLD = 300
const RED_THRESHOLD = 60

@Component({
  selector: 'deploy-banner',
  templateUrl: './deploy-banner.component.html',
  standalone: false,
})
export class DeployBannerComponent implements OnInit, OnDestroy {
  visible = false
  remaining = COUNTDOWN_SECONDS
  paused = false

  private _interval: ReturnType<typeof setInterval> | null = null
  private _unsubscribeAll = new Subject<void>()

  constructor(private _authService: AuthService, private _router: Router) {}

  get phase(): 'green' | 'yellow' | 'red' {
    if (this.remaining <= RED_THRESHOLD) return 'red'
    if (this.remaining <= YELLOW_THRESHOLD) return 'yellow'
    return 'green'
  }

  get progressPct(): number {
    return (this.remaining / COUNTDOWN_SECONDS) * 100
  }

  get formattedTime(): string {
    const m = Math.floor(this.remaining / 60)
    const s = this.remaining % 60
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  ngOnInit(): void {
    this._authService.newVersionAvailable$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((available) => {
        if (available && !this.visible) {
          this.visible = true
          this._startCountdown()
          this._listenForUserActivity()
        }
      })

    fromEvent(document, 'visibilitychange')
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => {
        if (document.visibilityState === 'visible') {
          this._authService.checkDeployVersion()
        }
      })
  }

  ngOnDestroy(): void {
    this._stopCountdown()
    this._unsubscribeAll.next()
    this._unsubscribeAll.complete()
  }

  update(): void {
    this._authService.clearStorageForNewVersion()
    this._authService.signOut().subscribe(() => {
      window.location.reload()
    })
  }

  @HostListener('window:beforeunload')
  onBeforeUnload(): void {
    if (this.visible) {
      this._authService.clearStorageForNewVersion()
    }
  }

  private _startCountdown(): void {
    this._interval = setInterval(() => {
      if (this.paused) return
      this.remaining--
      if (this.remaining <= 0) {
        this._stopCountdown()
        this.update()
      }
    }, 1000)
  }

  private _stopCountdown(): void {
    if (this._interval) {
      clearInterval(this._interval)
      this._interval = null
    }
  }

  private _listenForUserActivity(): void {
    merge(
      fromEvent(document, 'keydown'),
      fromEvent(document, 'input'),
    )
      .pipe(debounceTime(300), takeUntil(this._unsubscribeAll))
      .subscribe(() => {
        this.paused = true
        setTimeout(() => (this.paused = false), 10000)
      })
  }
}
